/**
 * Reset All Stores
 *
 * Central function to reset every Zustand store and HTTP cache to initial state.
 * MUST be called during logout to prevent data from the previous user
 * leaking into the next session (SPA navigation does NOT unmount stores).
 *
 * @module store/resetAllStores
 */

import { useOrganizationStore } from './organization/organizationStore';
import { useProjectsStore } from './projects/projectsStore';
import { useDashboardStore } from './dashboard/dashboardStore';
import { useDashboardDataStore } from './dashboard/dashboardDataStore';
import { useGenerationStore } from './generation/generationStore';
import { useTokenUsageStore } from './tokenUsage/tokenUsageStore';
import { useTokenUsageDataStore } from './tokenUsage/tokenUsageDataStore';
import { useAutomationStore } from './automation/automationStore';
import { useAutomationUIStore } from './automation/automationUIStore';
import { useRunStore } from './automation/runStore';
import { runStreamService } from '../shared/workers/RunStreamService';
import { useThemeStore } from './theme/themeStore';
import { applyThemeToDOM } from './theme/themeEffects';
import { clearAllCache } from '../shared/config/cachedAxiosConfig';
import organizationCache from '../features/organization/cache/OrganizationCache';
import logger from '../shared/services/logging/logger';

/**
 * Reset every store/cache that holds division- or organization-scoped data:
 * projects, dashboards, chats/generation, token usage, automation, run
 * streams, and all HTTP/organization caches. Shared by logout (via
 * `resetAllStores`) and by switching divisions (`DivisionSwitcher`), since
 * neither the axios cache keys nor these Zustand stores are aware of
 * divisionId — without this, the previous division's projects, dashboards,
 * chats and test runs stay on screen after a switch.
 *
 * Deliberately excludes `userStore` (the caller sets the new user/division
 * data itself) and `themeStore` (a per-user preference, not division data —
 * resetting it on a division switch would be wrong).
 */
export const resetDivisionScopedStores = () => {
  // ── Zustand stores ──────────────────────────────────────────

  // 1. Projects — clear cached project list and collaborator data
  useProjectsStore.setState({
    projects: [],
    loading: false,
    error: null,
    cache: { data: null, timestamp: 0 },
    collaboratorData: {},
    permissionsPreloaded: false
  });

  // 2. Dashboard UI — reset selected project and mode
  useDashboardStore.setState({
    selectedDashboardProject: '',
    dashboardMode: 'general',
    projectDisplayName: ''
  });

  // 3. Dashboard data — clear all cached test runs
  useDashboardDataStore.setState({
    testRunsCache: {}
  });

  // 4. Generation — clear background/completed chats, streaming state, and persisted sessions
  useGenerationStore.setState({
    isGenerating: false,
    isComplete: false,
    backgroundChats: [],
    completedChats: [],
    isAnalyzerStreaming: false,
    activeAnalyzerChat: null,
    activeScriptGeneration: null,
    xrayPendingJobs: []
  });

  // 5. Token Usage UI — reset filters, forecast, modal state
  useTokenUsageStore.setState({
    startDate: null,
    endDate: null,
    sortBy: 'cost',
    groupBy: 'day',
    limit: null,
    forecastEnabled: false,
    forecastDays: 7,
    activeQuickFilter: 'this-month',
    selectedUserId: null,
    showUserDetailsModal: false
  });

  // 6. Token Usage data — clear all cached API responses
  useTokenUsageDataStore.setState({
    cache: {}
  });

  // 7. Automation — reset core data and UI stores
  useAutomationStore.getState().reset();
  useAutomationUIStore.getState().resetUI();

  // 8. Run stream — disconnect worker and clear run data
  runStreamService.disconnect();
  useRunStore.getState().clearActiveRun();

  // ── HTTP / service caches ───────────────────────────────────

  // 9. Clear ALL axios HTTP response caches (9 cached instances with varying TTLs).
  //    Without this, projectCachedAxios (5-min TTL) can serve the previous
  //    division's/user's project list to the newly active division/session.
  try {
    clearAllCache();
  } catch (error) {
    logger.warn('Failed to clear axios caches', { error: error.message });
  }

  // 10. Clear organization Zustand store (members, invitations)
  useOrganizationStore.getState().reset();

  // 11. Clear organization data cache (members, divisions, groups, etc.)
  try {
    organizationCache.clearAll();
  } catch (error) {
    logger.warn('Failed to clear organization cache', { error: error.message });
  }
};

/**
 * Reset every Zustand store and HTTP cache to their default state.
 * MUST be called during logout to prevent data from the previous user
 * leaking into the next session (SPA navigation does NOT unmount stores).
 *
 * userStore is NOT reset here — it is already handled by
 * `updateUserData(null)` in the logout flow which resets it to
 * DEFAULT_USER_DATA and clears tokens/session.
 */
export const resetAllStores = () => {
  logger.info('Resetting all Zustand stores and caches on logout');

  resetDivisionScopedStores();

  // Theme — reset to default purple (prevent theme leaking between users;
  // logout-only, since it's a per-user preference, not division data).
  useThemeStore.setState({
    themeName: 'purple',
    isDarkMode: false
  });
  applyThemeToDOM('purple', false);

  logger.info('All stores and caches reset successfully');
};
